/* file: src/components/layout/SplitView */

import React from 'react';
import Viewer from '../viewers/Viewer';
import TabBar from '../tabs/TabBar';
import { Columns2 } from 'lucide-react';
import styles from './MainContent.module.css';

/**
 * Renders a split view layout with primary and secondary panels
 */
export default function SplitView({
  // Split mode and structure
  splitMode,
  
  // Element refs for drag resize
  topPanelRef,
  bottomPanelRef,
  leftPanelRef,
  rightPanelRef,
  
  // Drag handler functions
  handleVerticalDrag,
  handleHorizontalDrag,
  
  // Files
  primaryFile,
  secondaryFile,
  secondaryFiles,
  
  // File IDs
  primaryFileId,
  secondaryActiveId,
  
  // Display settings
  primaryPageToGo,
  primaryPageJump,
  secondaryPageToGo,
  secondaryPageJump,
  
  // Handlers
  onTabSelect,
  onTabClose,
  onExitSplit,
  
  // Empty state message
  emptyStateMessage
}) {
  // If no primary file, don't render split view
  if (!primaryFile) {
    return (
      <div className={styles.viewerArea}>
        <p className={styles.emptyStateMessage}>
          {emptyStateMessage || "No file selected for this split view."}
        </p>
      </div>
    );
  }

  // Ensure secondary files don't include the primary file to avoid duplicate keys
  const filteredSecondaryFiles = primaryFile 
    ? secondaryFiles.filter(file => file.id !== primaryFile.id)
    : secondaryFiles;

  // Render a tab bar with just the primary file and exit split button
  const renderPrimaryTab = () => (
    <div className={styles.primaryTabContainer}>
      <TabBar
        files={[primaryFile]}
        activeFileId={primaryFile.id}
        onTabSelect={onTabSelect}
        onTabClose={onTabClose}
        primaryMode={true}
      />
      <div 
        className={styles.exitSplitButton}
        title={splitMode === 'vertical' ? "Exit Vertical Split" : "Exit Horizontal Split"}
        onClick={onExitSplit}
      >
        <Columns2 size={18} strokeWidth={2} className={splitMode === 'vertical' ? 'rotate-90' : ''} />
      </div>
    </div>
  );

  // If vertical split (top/bottom)
  if (splitMode === 'vertical') {
    return (
      <div className={styles.splitContainerVertical}>
        {/* Top panel: primary file only */}
        <div ref={topPanelRef} className={styles.splitPanel}>
          {renderPrimaryTab()}
          <Viewer
            file={primaryFile}
            pageToGo={primaryPageToGo}
            pageJump={primaryPageJump}
            key={`viewer-top-${primaryFile.id}`}
          />
        </div>

        {/* Divider (draggable) */}
        <div 
          className={styles.splitDividerVertical}
          onMouseDown={handleVerticalDrag}
        />

        {/* Bottom panel: secondary tab bar + viewer */}
        <div ref={bottomPanelRef} className={styles.splitPanel}>
          <TabBar
            files={filteredSecondaryFiles}
            activeFileId={secondaryActiveId}
            onTabSelect={onTabSelect}
            onTabClose={onTabClose}
          />
          <Viewer 
            file={secondaryFile}
            pageToGo={secondaryPageToGo}
            pageJump={secondaryPageJump}
            key={`viewer-bottom-${secondaryFile?.id}`}
          />
          {!secondaryFile && (
            <p className={styles.emptyStateMessage}>
              {emptyStateMessage || "No secondary file selected for this split view."}
            </p>
          )}
        </div>
      </div>
    );
  } 
  // If horizontal split (left/right)
  else {
    return (
      <div className={styles.splitContainerHorizontal}>
        {/* Left panel: secondary tab bar + viewer */}
        <div ref={leftPanelRef} className={styles.splitPanel}>
          <TabBar
            files={filteredSecondaryFiles}
            activeFileId={secondaryActiveId}
            onTabSelect={onTabSelect}
            onTabClose={onTabClose}
          />
          <Viewer 
            file={secondaryFile}
            pageToGo={secondaryPageToGo}
            pageJump={secondaryPageJump}
            key={`viewer-left-${secondaryFile?.id}`}
          />
          {!secondaryFile && (
            <p className={styles.emptyStateMessage}>
              {emptyStateMessage || "No secondary file selected for this split view."}
            </p>
          )}
        </div>

        {/* Divider (draggable) */}
        <div 
          className={styles.splitDividerHorizontal}
          onMouseDown={handleHorizontalDrag}
        />

        {/* Right panel: primary file only */}
        <div ref={rightPanelRef} className={styles.splitPanel}>
          {renderPrimaryTab()}
          <Viewer
            file={primaryFile}
            pageToGo={primaryPageToGo}
            pageJump={primaryPageJump}
            key={`viewer-right-${primaryFile.id}`}
          />
        </div>
      </div>
    );
  }
} 